import React from 'react';
import { useQuery } from 'react-query';
import {
  BarChart3,
  Target,
  TrendingUp,
  DollarSign,
  Percent,
  Clock,
  Trophy,
  Activity,
  TrendingDown,
  Users,
  Calculator,
  Zap
} from 'lucide-react';
import api from '../../services/api';
import KPICard from './KPICard';
import toast from 'react-hot-toast';

const EventStatistics = ({ eventId }) => {
  // Query para obtener KPIs del evento
  const { data: kpisData, isLoading, error } = useQuery({
    queryKey: ['event-kpis', eventId],
    queryFn: () => api.get(`/kpis/events/${eventId}`).then(res => res.data),
    enabled: !!eventId,
    staleTime: 2 * 60 * 1000, // Cache por 2 minutos
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Error cargando estadísticas');
    }
  });

  // Query para obtener secuencias
  const { data: sequencesData, isLoading: sequencesLoading } = useQuery({
    queryKey: ['event-sequences', eventId],
    queryFn: () => api.get(`/kpis/events/${eventId}/sequences`).then(res => res.data),
    enabled: !!eventId,
    staleTime: 2 * 60 * 1000
  });

  const kpis = kpisData?.kpis;
  const currentSequence = sequencesData?.currentSequence;

  if (error) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Error Cargando Estadísticas
        </h3>
        <p className="text-red-600 dark:text-red-400">
          {error.response?.data?.message || 'No se pudieron cargar las estadísticas'}
        </p>
      </div>
    );
  }

  const getProfitabilityColor = (profitability) => {
    if (profitability > 20) return 'success';
    if (profitability > 0) return 'primary';
    if (profitability > -10) return 'warning';
    return 'danger';
  };

  const getDrawRateColor = (percentage) => {
    if (percentage >= 40) return 'success';
    if (percentage >= 30) return 'primary';
    if (percentage >= 20) return 'warning';
    return 'danger';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Estadísticas del Evento
        </h2>
        {kpis?.eventInfo && (
          <p className="text-gray-600 dark:text-gray-400">
            {kpis.eventInfo.name} • Paso {kpis.eventInfo.currentStep} de {kpis.eventInfo.maxSteps}
            ({kpis.eventInfo.progress}% completado)
          </p>
        )}
      </div>

      {/* KPIs Principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total Partidos"
          value={kpis?.totalSteps}
          subtitle="Pasos jugados"
          icon={Activity}
          color="primary"
          loading={isLoading}
          tooltip="Número total de pasos/partidos jugados en este evento"
        />

        <KPICard
          title="Total Empates"
          value={kpis?.totalDraws}
          subtitle={`${kpis?.drawPercentage || 0}% del total`}
          icon={Target}
          color={getDrawRateColor(kpis?.drawPercentage)}
          loading={isLoading}
          tooltip="Cantidad de empates obtenidos y su porcentaje"
        />

        <KPICard
          title="Total Participantes"
          value={kpis?.totalParticipants}
          subtitle="Jugadores activos"
          icon={Users}
          color="info"
          loading={isLoading}
          tooltip="Número de participantes activos en el evento"
        />

        <KPICard
          title="Rentabilidad"
          value={kpis?.profitability ? `${kpis.profitability}%` : '-'}
          subtitle={kpis?.totalProfit >= 0 ? 'Ganancia' : 'Pérdida'}
          icon={kpis?.profitability >= 0 ? TrendingUp : TrendingDown}
          color={getProfitabilityColor(kpis?.profitability)}
          loading={isLoading}
          tooltip="Rentabilidad calculada: (Ingresos - Inversión) / Inversión * 100"
        />
      </div>

      {/* KPIs Financieros */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <DollarSign className="w-5 h-5 mr-2" />
          Indicadores Financieros
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <KPICard
            title="Total Inversión"
            value={kpis?.totalInvestment}
            subtitle="Dinero apostado"
            icon={DollarSign}
            color="neutral"
            loading={isLoading}
            tooltip="Suma total de todas las apuestas realizadas"
          />

          <KPICard
            title="Total Ingresos"
            value={kpis?.totalIncome}
            subtitle="Dinero ganado"
            icon={Trophy}
            color="success"
            loading={isLoading}
            tooltip="Suma total de ganancias por empates"
          />

          <KPICard
            title="Ganancia Neta"
            value={kpis?.totalProfit}
            subtitle={kpis?.totalProfit >= 0 ? 'Positiva' : 'Negativa'}
            icon={kpis?.totalProfit >= 0 ? TrendingUp : TrendingDown}
            color={kpis?.totalProfit >= 0 ? 'success' : 'danger'}
            loading={isLoading}
            tooltip="Diferencia entre ingresos totales e inversión total"
          />
        </div>
      </div>

      {/* KPIs de Apuestas */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <Calculator className="w-5 h-5 mr-2" />
          Métricas de Apuestas
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Apuesta Máxima"
            value={kpis?.maxBetAmount}
            subtitle="Por paso individual"
            icon={Zap}
            color="warning"
            loading={isLoading}
            tooltip="Apuesta más alta realizada en un solo paso"
          />

          <KPICard
            title="Inversión Máxima"
            value={kpis?.maxCumulativeInvestment}
            subtitle="Acumulada"
            icon={TrendingUp}
            color="danger"
            loading={isLoading}
            tooltip="Máxima inversión acumulada alcanzada"
          />

          <KPICard
            title="Factor Promedio"
            value={kpis?.avgPayoutFactor}
            subtitle="Multiplicador"
            icon={Percent}
            color="primary"
            loading={isLoading}
            tooltip="Factor de pago promedio en empates"
          />

          <KPICard
            title="Cuota Promedio"
            value={kpis?.avgDrawOdds}
            subtitle="Empate"
            icon={Target}
            color="info"
            loading={isLoading}
            tooltip="Cuota promedio ofrecida para empates"
          />
        </div>
      </div>

      {/* KPIs de Balances */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          Análisis de Balances
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <KPICard
            title="Saldo Mínimo"
            value={kpis?.minBalance}
            subtitle="Punto más bajo"
            icon={TrendingDown}
            color="danger"
            loading={isLoading}
            tooltip="Saldo más bajo alcanzado durante el evento"
          />

          <KPICard
            title="Saldo Máximo"
            value={kpis?.maxBalance}
            subtitle="Punto más alto"
            icon={TrendingUp}
            color="success"
            loading={isLoading}
            tooltip="Saldo más alto alcanzado durante el evento"
          />
        </div>
      </div>

      {/* Secuencias */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2" />
          Análisis de Secuencias
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <KPICard
            title="Máxima Secuencia de Empates"
            value={kpis?.maxDrawSequence}
            subtitle="Empates consecutivos"
            icon={Trophy}
            color="success"
            loading={isLoading}
            tooltip="Máximo número de empates consecutivos"
          />

          <KPICard
            title="Máxima Secuencia Sin Empate"
            value={kpis?.maxNoDrawSequence}
            subtitle="Sin empates consecutivos"
            icon={Target}
            color="warning"
            loading={isLoading}
            tooltip="Máximo número de partidos consecutivos sin empate"
          />

          {currentSequence && (
            <KPICard
              title="Secuencia Actual"
              value={currentSequence.sequence_length}
              subtitle={`${currentSequence.sequence_type === 'draw' ? 'Empates' : 'Sin empates'} consecutivos`}
              icon={currentSequence.sequence_type === 'draw' ? Trophy : Target}
              color={currentSequence.sequence_type === 'draw' ? 'success' : 'warning'}
              loading={sequencesLoading}
              tooltip="Secuencia activa en curso"
            />
          )}
        </div>
      </div>

      {/* Footer con última actualización */}
      {kpis?.calculatedAt && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-dark-600">
          Última actualización: {new Date(kpis.calculatedAt).toLocaleString('es-CO')}
        </div>
      )}
    </div>
  );
};

export default EventStatistics;